import { convertToTimestampApi } from '@core/logic/helper/timeFormatting';
import { toHumanReadable } from '@core/logic';
import { useGetPhotoCountQuery } from '@core/redux/query';
import { ArrowBoxOut, FolderOpenOutline, PinFilled, PinOutline } from '@deepup/icons';
import { projectsHistoryActions, projectsHistorySelectors } from '@menu/redux/projectsHistory';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  GridCellClassNamePropType,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridValueGetter,
} from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useScanQaColors } from '@projects/pages/detail/menu/ScanOrganization/hooks';
import { formatProjectLabelingLabel } from '@projects/pages/list/filterProjectsByLabeling';
import { formatProjectActivityStateLabel } from '@projects/pages/list/filterProjectsByActivityState';
import { projectSelectors } from '@projects/redux';
import type { Project } from '@projects/types';
import { getOneDriveLink } from '@projects/utils/onedrive';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const PinButton = ({ row }: GridCellParams<Project>) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const pinnedProjects = useSelector(projectsHistorySelectors.selectPinnedProjects);

  const isPinned = pinnedProjects.some((p) => p.id === row.id);

  return (
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        if (isPinned) {
          dispatch(projectsHistoryActions.dropFromPinned(row.id));
        } else {
          dispatch(projectsHistoryActions.addToPinned({ id: row.id, title: row.name }));
        }
      }}
    >
      {isPinned ? (
        <PinFilled fill={theme.palette.primary.main} />
      ) : (
        <PinOutline fill={theme.palette.grey[600]} />
      )}
    </IconButton>
  );
};

const NewTabButton = ({ params }: { params: GridRenderCellParams<Project> }) => {
  const theme = useTheme();
  const { row } = params;

  return (
    <IconButton href={`/projects/${row.id}`} target="_blank">
      <ArrowBoxOut fill={theme.palette.grey[600]} />
    </IconButton>
  );
};

const OneDriveLink = ({ params }: { params: GridRenderCellParams<Project> }) => {
  const theme = useTheme();
  const { row } = params;

  const url = getOneDriveLink(row);

  return (
    <IconButton href={url} target="_blank">
      <FolderOpenOutline fill={theme.palette.grey[600]} />
    </IconButton>
  );
};

const ProjectPhotos = ({ params }: { params: GridRenderCellParams<Project> }) => {
  const { row } = params;
  const theme = useTheme();
  const recordedAfter = useMemo(
    () => convertToTimestampApi(DateTime.now().startOf('hour').minus({ hours: 72 }).toISO()),
    [],
  );

  const { data: recentPhotos } = useGetPhotoCountQuery({
    projectId: row.id,
    recordedAfter,
  });

  const { data: allPhotos } = useGetPhotoCountQuery({
    projectId: row.id,
  });

  const totalPhotos = Number(allPhotos?.paginationInfo?.totalItems ?? 0);
  const recentPhotosCount = Number(recentPhotos?.paginationInfo?.totalItems ?? 0);

  const recentColor =
    recentPhotosCount > 0 ? theme.palette.primary.main : theme.palette.text.secondary;

  return (
    <Tooltip title={`Last 72 hours: ${recentPhotosCount} photos / Total: ${totalPhotos} photos`}>
      <Stack direction="row" gap={1} alignItems="center">
        <Typography
          component="span"
          variant="body2"
          sx={{
            color: recentColor,
          }}
        >
          {recentPhotosCount}
        </Typography>
        <span>/</span>
        <Typography component="span" variant="body2">
          {totalPhotos}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const getQAStateFromField = (field: string) => field.split('.')[1] as keyof Project['scanCounts'];

const getScanCountValue: GridValueGetter<Project> = (_, project, { field }) =>
  project.scanCounts[getQAStateFromField(field)] || 0;

const getCellClassName: GridCellClassNamePropType<Project> = (params) =>
  params.value !== 0 ? getQAStateFromField(params.field) : 'qa_empty';

const waitPlanDataUpdateLabel = toHumanReadable('WAITING_FOR_PLAN_DATA_UPDATE');

const buildProjectsTableColumns = (): GridColDef<Project>[] => [
  { field: 'name', headerName: 'Project Name', flex: 1, minWidth: 300 },
  {
    field: 'labelingType',
    headerName: 'Labeling type',
    width: 140,
    renderCell: (params) => (
      <Typography variant="body2">{formatProjectLabelingLabel(params.row.labelingType)}</Typography>
    ),
  },
  {
    field: 'activityState',
    headerName: 'Project state',
    width: 130,
    valueGetter: (_, row) => formatProjectActivityStateLabel(row.activityState),
    renderCell: (params) => (
      <Typography variant="body2">
        {formatProjectActivityStateLabel(params.row.activityState)}
      </Typography>
    ),
  },
  {
    field: 'newTab',
    renderHeader: () => null,
    width: 10,
    type: 'actions',
    renderCell: (params) => <NewTabButton params={params} />,
  },
  {
    field: 'onedrive',
    renderHeader: () => null,
    width: 10,
    type: 'actions',
    renderCell: (params) => <OneDriveLink params={params} />,
  },
  {
    field: 'scanCounts.preToLabel',
    headerName: 'Upcoming',
    type: 'number',
    width: 95,
    valueGetter: getScanCountValue,
    cellClassName: getCellClassName,
  },
  {
    field: 'scanCounts.waitPlanDataUpdate',
    headerName: waitPlanDataUpdateLabel,
    renderHeader: () => (
      <Tooltip title={waitPlanDataUpdateLabel} arrow placement="top">
        <Typography
          variant="body2"
          noWrap
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
        >
          {waitPlanDataUpdateLabel}
        </Typography>
      </Tooltip>
    ),
    type: 'number',
    width: 95,
    valueGetter: getScanCountValue,
    cellClassName: getCellClassName,
  },
  {
    field: 'scanCounts.qaToLabel',
    headerName: 'To label',
    type: 'number',
    width: 95,
    valueGetter: getScanCountValue,
    cellClassName: getCellClassName,
  },
  {
    field: 'scanCounts.qaToRework',
    headerName: 'To rework',
    type: 'number',
    width: 95,
    valueGetter: getScanCountValue,
    cellClassName: getCellClassName,
  },
  {
    field: 'scanCounts.qaToClarify',
    headerName: 'To clarify',
    type: 'number',
    width: 95,
    valueGetter: getScanCountValue,
    cellClassName: getCellClassName,
  },
  {
    field: 'scanCounts.qaReady',
    headerName: 'QA ready',
    type: 'number',
    width: 95,
    valueGetter: getScanCountValue,
    cellClassName: getCellClassName,
  },
  {
    field: 'scanCounts.qaDone',
    headerName: 'Done',
    type: 'number',
    width: 95,
    valueGetter: getScanCountValue,
    cellClassName: getCellClassName,
  },
  {
    field: 'scanCounts.total',
    headerName: 'Total',
    type: 'number',
    width: 95,
    valueGetter: getScanCountValue,
  },
  {
    field: 'photos',
    headerName: 'Photos',
    width: 120,
    sortable: false,
    type: 'string',
    renderCell: (params) => {
      return <ProjectPhotos params={params} />;
    },
  },
  {
    field: 'updatedAt',
    headerName: 'Updated at',
    type: 'date',
    width: 165,
    valueFormatter: (value) => DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT),
  },
  {
    field: 'pin',
    headerName: 'Pinned Projects',
    renderHeader: () => null,
    width: 10,
    sortable: false,
    filterable: false,
    renderCell: PinButton,
  },
];

// max page size in xGrid free tier
const MAX_PAGE_SIZE = 100;

export const ProjectsTable = ({
  rows,
  pagination,
  hideFooter,
  search,
}: {
  rows: Project[];
  pagination?: true;
  hideFooter?: true;
  search: string;
}) => {
  const navigate = useNavigate();
  const { qaColumnColors } = useScanQaColors();
  const totalProjects = useSelector(projectSelectors.selectTotal);

  const columns = buildProjectsTableColumns();

  const overlapsMaxPageSize = rows.length > MAX_PAGE_SIZE ? true : undefined;

  const initialState = pagination
    ? {
        pagination: { paginationModel: { pageSize: 12 } },
      }
    : overlapsMaxPageSize
      ? { pagination: { paginationModel: { pageSize: MAX_PAGE_SIZE } } }
      : {};

  return (
    <DataGridPro<Project>
      loading={!rows.length && totalProjects === 0}
      disableColumnMenu
      sortingMode="client"
      filterModel={{ items: [{ field: 'name', operator: 'contains', value: search }] }}
      onRowClick={({ row }, event) => {
        const url = `/projects/${(row as Project).id}`;
        event.ctrlKey || event.metaKey ? window.open(url, '_blank') : navigate(url);
      }}
      sx={(theme) => ({
        '& .waitPlanDataUpdate': {
          color: qaColumnColors.WAITING_FOR_PLAN_DATA_UPDATE,
        },
        '& .qaToLabel': {
          color: qaColumnColors.TO_LABEL,
        },
        '& .qaToRework': {
          color: qaColumnColors.TO_REWORK,
        },
        '& .qaToClarify': {
          color: qaColumnColors.TO_CLARIFY,
        },
        '& .qaReady': {
          color: qaColumnColors.QA_READY,
        },
        '& .qaDone': {
          color: qaColumnColors.DONE,
        },
        '& .qa_empty': {
          color: theme.palette.text.secondary,
        },
      })}
      rows={rows}
      columns={columns}
      pagination={pagination || overlapsMaxPageSize}
      hideFooter={hideFooter && !overlapsMaxPageSize}
      initialState={initialState}
      pageSizeOptions={[12, 25, 50, 100]}
    />
  );
};
